import { ApiError } from '../identity/types';
import { friendlyErrorMessage } from '../../shared/friendlyError';
import { JobRoleErrorCode } from './types';

/** Never matches on message text for logic: only the HTTP status and the API's stable `code`. */
const RAW_FALLBACK = /^Request to .+ failed with \d+$/;

/** A 403 for a missing PERMISSION. A subscription refusal (`SUBSCRIPTION_UPGRADE_REQUIRED` for the template library, or a subscription that is not in
 * force) is also a 403 but is about the company's plan, not the person's role: it must never switch a screen to read-only or say "no permission". */
export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && !(err.code ?? '').startsWith('SUBSCRIPTION');
}

export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export function isUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503;
}

export function isConcurrencyConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === JobRoleErrorCode.ConcurrencyConflict;
}

export function isNotDraft(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === JobRoleErrorCode.NotDraft;
}

export function isPayBandReviewRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400 && err.code === JobRoleErrorCode.PayBandReviewRequired;
}

export const PAY_BAND_CONFLICT_MESSAGE =
  'This pay band was changed by someone else after you opened it, so your changes were not saved. Reload the latest pay band, then make your changes again.';

export const CONCURRENCY_CONFLICT_MESSAGE =
  'This role was changed by someone else after you opened it, so your edits were not saved. Reload the latest version, then make your changes again.';

/**
 * The Roles API reports a rule or state refusal (a duplicate title, a role that is not a Draft, a draft that already exists, a
 * role that cannot be assigned) as a 400 or 409 with a hand-authored, user-safe `detail`. The shared `friendlyErrorMessage` replaces
 * every 409 with a generic line, which would hide that guidance, so those two statuses show the server's detail here; a concurrency
 * conflict gets its own plain-language copy; everything else (401, 403, 429, 5xx, unknown) goes through the shared helper unchanged.
 */
export function jobRoleErrorMessage(err: unknown, fallback: string): string {
  if (isConcurrencyConflict(err)) return CONCURRENCY_CONFLICT_MESSAGE;
  if (err instanceof ApiError && (err.status === 400 || err.status === 409) && err.message && !RAW_FALLBACK.test(err.message)) return err.message;
  if (isForbidden(err)) return 'You do not have permission to do that. Ask a company administrator if you need access to job roles.';
  return friendlyErrorMessage(err, fallback);
}
